import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import AppLayout from '../../../../component/appLayout';
import { ActivityCard, HeaderBlock, ActivityContainer, Head, ButtonComponent, ActivitySection, Empty } from '../styles';
import ActivityTimelineComponent from '../activityTimeline';
import { categorizeActivityLogs } from '../activityUtils';
import { rootStore } from '../../../../mobx/rootStore';
import { TaskActivityLogInterface } from '../../../../mobx/interfaces/project';
import { getProjectWiseActivity } from '../../../../services/projectServices';
import { observer } from 'mobx-react-lite';
import IconButton from '../../../../component/button/iconButton';
import { BUTTON_TYPE, EMPTY_STATE_SIZE, ICON_BUTTON_SIZE, ICON_BUTTON_TYPE } from '../../../../global/constants';
import { isEmpty } from '../../../../helpers/common';
import Button from '../../../../component/button';
import ReportTimelineLoading from '../../../../component/loading/reportTimeLineLoading';
import EmptyStateV2 from '../../../../component/emptyState';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useMobileDevice } from '../../../../global/useMobile';
import ResponsiveHeader from '../../../../component/responsiveHeader';
import SubNavigation from '../../../../component/bottomNavigation/subNavigation';
import { ROUTES } from '../../../../routes/urls';
dayjs.extend(relativeTime);

const AllActivities: React.FC = () => {
  //Mobx store variables
  const { projectStore, workspaceStore } = rootStore;
  const { currentWorkspace } = workspaceStore || {};
  const { currentProject, projects } = projectStore || {};
  // State variables
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(100);
  const [hasMore, setHasMore] = useState(true);
  //Other variables
  const params: { id: string } = useParams();
  const history = useHistory();
  const isMobile = useMobileDevice();

  const handleBack = useCallback(() => {
    history.push(ROUTES.PROJECT_OVERVIEW.replace(':id', params?.id || ''));
  }, [history, params?.id]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      if (isEmpty(currentWorkspace?.id) || projects?.length === 0) return;
      if (params?.id || !currentProject?.id) {
        rootStore.projectStore.setCurrentProject(params.id);
      }
      const data: TaskActivityLogInterface[] | false = await getProjectWiseActivity(params.id, 1, limit);
      setPage(1);
      setHasMore(Array.isArray(data) ? data.length === limit : false);
    } catch (e) {
      console.log('Error : ', e);
    } finally {
      setLoading(false);
    }
  }, [currentProject?.id, currentWorkspace?.id, limit, params.id, projects?.length]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Categorize activity logs in a single pass using shared helper
  const categorizedLogs = useMemo(
    () => categorizeActivityLogs(currentProject?.activityLogs || []),
    [currentProject?.activityLogs]
  );

  const handleLoadMore = useCallback(async () => {
    try {
      setLoading(true);
      const nextPage = page + 1;
      const data: TaskActivityLogInterface[] | false = await getProjectWiseActivity(params.id, nextPage, limit);
      if (Array.isArray(data)) {
        setPage(nextPage);
        setHasMore(data.length === limit);
      } else {
        setHasMore(false);
      }
    } catch (e) {
      console.log('Error : ', e);
    } finally {
      setLoading(false);
    }
  }, [limit, page, params.id]);

  const showLoadMoreButton = hasMore && (currentProject?.activityLogs?.length || 0) > 0;

  return (
    <>
      <AppLayout>
        {isMobile ? (
          <ResponsiveHeader title='All Activities' subTitle={currentProject?.name} noMargin={true} />
        ) : (
          <HeaderBlock>
            <IconButton
              iconName='new-large-back-arrow-icon'
              size={ICON_BUTTON_SIZE.SMALL}
              variant={ICON_BUTTON_TYPE.TERTIARY}
              onClick={handleBack}
            />
            <span>{currentProject?.name}</span>
          </HeaderBlock>
        )}
        <ActivityContainer>
          <ActivitySection>
            {!isMobile && <Head>All Activities</Head>}
            {(!currentProject?.activityLogs || currentProject?.activityLogs?.length === 0) && loading && (
              <ReportTimelineLoading />
            )}
            {(!currentProject?.activityLogs || currentProject?.activityLogs?.length === 0) && !loading && (
              <Empty>
                <EmptyStateV2 size={EMPTY_STATE_SIZE.SMALL} imageUrl={'gif_empty'} description='No data found' />
              </Empty>
            )}
            {currentProject?.activityLogs && currentProject?.activityLogs?.length > 0 && (
              <ActivityCard>
                <ActivityTimelineComponent title='Today' activities={categorizedLogs.today} />
                <ActivityTimelineComponent title='This week' activities={categorizedLogs.week} />
                <ActivityTimelineComponent title='This month' activities={categorizedLogs.month} />
                <ActivityTimelineComponent title='Older' activities={categorizedLogs.older} />
              </ActivityCard>
            )}
          </ActivitySection>
          {showLoadMoreButton && (
            <ButtonComponent>
              <Button
                variant={BUTTON_TYPE.SECONDARY}
                buttonType='button'
                title={loading ? 'Loading...' : 'Load More'}
                size='small'
                onClick={handleLoadMore}
              />
            </ButtonComponent>
          )}
        </ActivityContainer>
      </AppLayout>
      {/* bottom navigation for responsive screen */}
      <SubNavigation />
    </>
  );
};

export default observer(AllActivities);
